import { DateTime } from "luxon";
import { useCallback, useState, type JSX } from "react";
import { CaptureDateList } from "../timeline/capture-date-list";
import { useSelectableBounds } from "./calendar-utils";
import {
  CaptureGranularity,
  CaptureLayout,
  ViewProps,
} from "./capture-calendar-types";
import { DayCalendar } from "./day-calendar";
import { MonthGrid } from "./month-grid";

type CaptureCalendarProps = Pick<
  ViewProps,
  | "stats"
  | "startDate"
  | "endDate"
  | "onPick"
  | "onHover"
  | "onPress"
  | "isDragging"
> &
  Partial<Pick<ViewProps, "isPickable">> & {
    /** The month the calendar opens on */
    initialMonth: DateTime;
    /** Whether to list the capture dates or show a calendar */
    layout: CaptureLayout;
    /** True while Start or End is being edited, when a month click only opens its days */
    isEditing?: boolean;
  };

/**
 * @returns The capture dates as a timeline list, or as a calendar of days or months. The
 * calendar opens on months, unless a range of partial months is set, then on its days.
 */
export function CaptureCalendar({
  initialMonth,
  layout,
  isPickable = () => true,
  isEditing,
  ...rest
}: CaptureCalendarProps): JSX.Element {
  const { startDate, endDate, stats } = rest;
  const [month, setMonth] = useState(initialMonth);
  const [granularity, setGranularity] = useState(() =>
    startDate && endDate && !isWholeMonths(startDate, endDate)
      ? CaptureGranularity.day
      : CaptureGranularity.month,
  );
  const bounds = useSelectableBounds(stats);

  // Lands on the range if one is set, else on the year's first month with captures
  const onToggleView = useCallback(() => {
    const isDayView = granularity === CaptureGranularity.day;
    if (startDate) {
      setMonth(DateTime.fromJSDate(startDate));
    } else if (!isDayView) {
      const first = stats.days.find(
        ({ start }) => start.getFullYear() === month.year,
      );
      if (first) setMonth(DateTime.fromJSDate(first.start));
    }
    setGranularity(
      isDayView ? CaptureGranularity.month : CaptureGranularity.day,
    );
  }, [granularity, startDate, stats.days, month]);

  const props: ViewProps = {
    ...rest,
    isPickable,
    month,
    onMonthChange: setMonth,
    bounds,
    onToggleView,
  };

  if (layout === CaptureLayout.list) return <CaptureDateList {...props} />;
  return granularity === CaptureGranularity.day ? (
    <DayCalendar {...props} />
  ) : (
    <MonthGrid
      {...props}
      // While editing, a month click only navigates, so there is no range to preview
      onHover={isEditing ? undefined : rest.onHover}
      onPick={(date, unit, isShiftHeld) => {
        if (isShiftHeld || !isEditing) rest.onPick(date, unit, isShiftHeld);
        // A plain click inspects the month; shift-clicks keep building the range on months
        if (isShiftHeld) return;
        setMonth(date);
        setGranularity(CaptureGranularity.day);
      }}
    />
  );
}

/**
 * @returns True when the range starts on a month's first day and ends on a month's last
 * @param start Inclusive start of the range
 * @param end Inclusive end of the range
 */
function isWholeMonths(start: Date, end: Date): boolean {
  const last = DateTime.fromJSDate(end);
  return start.getDate() === 1 && last.hasSame(last.endOf("month"), "day");
}
